/**
 * CSP Violation Monitor
 *
 * Monitors and logs Content Security Policy violations during development.
 * Import in index.jsx: import './utils/cspMonitor';
 */

interface CSPViolation {
  documentUri: string;
  violatedDirective: string;
  effectiveDirective: string;
  originalPolicy: string;
  blockedUri: string;
  statusCode: number;
  sourceFile?: string;
  lineNumber?: number;
  columnNumber?: number;
  sample?: string;
}

/**
 * Statistics tracking
 */
const stats = {
  totalViolations: 0,
  violationsByDirective: new Map<string, number>(),
  violationsByUri: new Map<string, number>(),
};

/**
 * Main CSP violation handler
 */
const handleCSPViolation = (event: SecurityPolicyViolationEvent): void => {
  const violation: CSPViolation = {
    documentUri: event.documentURI,
    violatedDirective: event.violatedDirective,
    effectiveDirective: event.effectiveDirective,
    originalPolicy: event.originalPolicy,
    blockedUri: event.blockedURI,
    statusCode: event.statusCode,
    sourceFile: event.sourceFile,
    lineNumber: event.lineNumber,
    columnNumber: event.columnNumber,
    sample: event.sample,
  };

  // Update statistics
  stats.totalViolations++;
  const directive = violation.effectiveDirective || violation.violatedDirective;
  stats.violationsByDirective.set(directive, (stats.violationsByDirective.get(directive) || 0) + 1);
  const uri = violation.blockedUri || 'unknown';
  stats.violationsByUri.set(uri, (stats.violationsByUri.get(uri) || 0) + 1);

  // Log violation
  console.group('🚨 CSP Violation');
  console.error('Blocked:', violation.blockedUri);
  console.error('Directive:', violation.violatedDirective);
  if (violation.sourceFile) {
    console.error('Source:', `${violation.sourceFile}:${violation.lineNumber}`);
  }
  console.groupEnd();
};

/**
 * Display CSP violation statistics
 */
const displayStats = (): void => {
  if (stats.totalViolations === 0) {
    console.log('✅ No CSP violations detected');
    return;
  }

  console.group('📊 CSP Violations');
  console.log('Total:', stats.totalViolations);
  if (stats.violationsByDirective.size > 0) {
    console.table(
      Array.from(stats.violationsByDirective.entries()).map(([directive, count]) => ({
        Directive: directive,
        Count: count,
      }))
    );
  }
  console.groupEnd();
};

/**
 * Initialize CSP monitoring
 */
const initCSPMonitoring = (): void => {
  document.addEventListener('securitypolicyviolation', handleCSPViolation);

  if (typeof window !== 'undefined') {
    interface CSPStatsWindow extends Window {
      cspStats: {
        display: () => void;
        clear: () => void;
      };
    }
    (window as unknown as CSPStatsWindow).cspStats = {
      display: displayStats,
      clear: () => {
        stats.totalViolations = 0;
        stats.violationsByDirective.clear();
        stats.violationsByUri.clear();
        console.log('✅ CSP statistics cleared');
      },
    };
  }

  console.log('🛡️ CSP Monitor active. Use window.cspStats.display() to view violations.');
};

// Auto-initialize
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCSPMonitoring);
  } else {
    initCSPMonitoring();
  }
}

export default { displayStats };
